import { useQuery } from "react-query";
import Employer from "../apiResources/Employer";
import Recruiter from "../apiResources/Recruiter";
import EmployerUser from "../apiResources/EmployerUser";
import RecruiterUser from "../apiResources/RecruiterUser";
import { useCurrentUser } from "./user";
import { PaginationQuery } from "./types";

export const updateEmployerCompany = async (fields: Employer["attributes"]) => {
  const { id, ...others } = fields;

  const employer = new Employer({ id: String(id) });
  employer.isPersisted = true;
  employer.assignAttributes(others);

  const success = await employer.save();

  return {
    attributes: employer.attributes,
    validationErrors: success ? null : employer.errors,
  };
};

export const updateRecruiterCompany = async (
  fields: Recruiter["attributes"]
) => {
  const { id, ...others } = fields;

  const recruiter = new Recruiter({ id: String(id) });
  recruiter.isPersisted = true;
  recruiter.assignAttributes(others);

  const success = recruiter.save();

  return {
    attributes: recruiter.attributes,
    validationErrors: success ? null : recruiter.errors,
  };
};

export const useRecruiter = () => {
  const { data: user } = useCurrentUser();

  const recruiterId = user?.companyId;

  return useQuery(
    ["recruiter", recruiterId],
    async () => (await Recruiter.find(recruiterId)).data,
    {
      enabled: !!recruiterId,
      refetchOnWindowFocus: false,
    }
  );
};

interface TeamMembersQuery extends PaginationQuery {
  query?: string;
}

export const useTeamMembers = (params: TeamMembersQuery = {}) => {
  const { data: user } = useCurrentUser();

  const companyId = user?.companyId;

  const fetchTeamMembers = async () => {
    const User = user.isRecruiter ? RecruiterUser : EmployerUser;
    const response = await User.where({
      company_id: companyId,
      full_name: { match: params.query },
    })
      .page(params.page)
      .per(params.per)
      .stats({ total: "count" })
      .all();

    return {
      members: response.data as (RecruiterUser | EmployerUser)[],
      meta: {
        total: response.meta.stats.total.count,
      },
    };
  };

  return useQuery(["teamMembers", { companyId, ...params }], fetchTeamMembers, {
    enabled: !!companyId,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};
